import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../contexts/PermissionContext';
import { API_URLS } from "../../config/apiConfig";
import { getAdminAITestPreviewUrl } from '../../utils/adminAITestImageUtils';
import SecureImage from '../Common/SecureImage';
import SecureVideo from '../Common/SecureVideo';
import {
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  Box,
  Typography,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import './AdminAITest.css';

// Helper function to get secure admin AI test image URL
const getSecureAdminAITestUrl = (imagePath, generationId, isInputImage = false, imageType = null) => {
  if (!imagePath) return null;

  // If it's already a full URL (starts with http), return as-is
  if (imagePath.startsWith('http')) {
    return imagePath;
  }

  // For input images, use the admin preview API with type=input
  if (isInputImage && generationId) {
    return getAdminAITestPreviewUrl(generationId, 'input');
  }

  // For enhanced images, use the admin preview API with type=enhanced
  if (imageType === 'enhanced' && generationId) {
    return getAdminAITestPreviewUrl(generationId, 'enhanced');
  }

  // For output images (generated by admin AI test), use the secure preview API
  if (!isInputImage && generationId) {
    return getAdminAITestPreviewUrl(generationId);
  }

  // For other relative paths, assume they're in uploads
  return `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/uploads/${imagePath}`;
};

const AdminAITest = () => {
  const { hasPermission } = usePermissions();

  // State management
  const [activeTab, setActiveTab] = useState('test-form');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [info, setInfo] = useState('');

  // Form state
  const [generationType, setGenerationType] = useState('image'); // 'image' or 'video'
  const [formData, setFormData] = useState({
    // Single API config (for image generation)
    api_url: '',
    api_key: '',
    model: '',

    // Dual API config (for video generation)
    step1_api_url: '',
    step1_api_key: '',
    step1_model: '',
    step2_api_url: '',
    step2_api_key: '',
    step2_model: '',

    // Common fields
    s3_folder_path: '',
    folder_name: '',  // User-defined folder name for uploads
    prompt: '',
    image_prompt: '',  // Keep for backward compatibility
    image_prompt_1: '',  // Step 1: image enhancement
    video_prompt: '',    // Step 2: video generation
    output_format: 'image',
    guidance_scale: 7.5,
    ratio: '1:1',
    resolution: '2k',
    model_endpoint: '',
  });

  // API testing state
  const [apiTestingStatus, setApiTestingStatus] = useState({
    step1: { testing: false, success: false, error: '' },
    step2: { testing: false, success: false, error: '' }
  });

  // S3 images state
  const [s3Images, setS3Images] = useState([]);
  const [s3Loading, setS3Loading] = useState(false);

  // Folder upload state
  const [folderUploadMode, setFolderUploadMode] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  // Folder management state
  const [existingFolders, setExistingFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [uploadMode, setUploadMode] = useState('create_new'); // 'create_new' or 'append_existing'
  const [showFolderBrowser, setShowFolderBrowser] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);

  // Current batch state with persistence
  const [currentBatch, setCurrentBatch] = useState(() => {
    const saved = localStorage.getItem('admin_ai_test_current_batch');
    return saved ? JSON.parse(saved) : null;
  });
  const [batchStatus, setBatchStatus] = useState(() => {
    const saved = localStorage.getItem('admin_ai_test_batch_status');
    return saved ? JSON.parse(saved) : null;
  });
  const [batchResults, setBatchResults] = useState(() => {
    const saved = localStorage.getItem('admin_ai_test_batch_results');
    return saved ? JSON.parse(saved) : [];
  });

  // History state - using same pagination pattern as FeedbackManagement.js
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({
    status: '',
    output_format: '',
    date_from: '',
    date_to: ''
  });

  // Pagination state - using same pattern as FeedbackManagement.js
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageLoadErrors, setImageLoadErrors] = useState({});

  // Collage view state
  const [collageMode, setCollageMode] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(0.5);
  const [collageImagesLoaded, setCollageImagesLoaded] = useState({ input: false, output: false });

  // Prompt Library state - for loading prompts from library
  const [promptLibrary, setPromptLibrary] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [processedPrompt, setProcessedPrompt] = useState(null);
  const [replacedVariables, setReplacedVariables] = useState([]);
  const [promptLoading, setPromptLoading] = useState(false);

  // Master data for ethnicity, clothing type, bust type and bust color selection
  const [masterData, setMasterData] = useState({
    ethnicities: [],
    clothing_types: [],
    bust_finishes: [],
    bust_colors: []
  });
  const [selectedEthnicity, setSelectedEthnicity] = useState('');
  const [selectedClothingType, setSelectedClothingType] = useState('');
  const [selectedBustFinish, setSelectedBustFinish] = useState('');
  const [selectedBustColor, setSelectedBustColor] = useState('');

  // Check permissions
  const canView = hasPermission('AI Test Module', 'view');
  const canCreate = hasPermission('AI Test Module', 'create');
  const canUpdate = hasPermission('AI Test Module', 'update');
  const canDelete = hasPermission('AI Test Module', 'delete');

  // API base URL
  const API_BASE = `${API_URLS.ADMIN_BASE}/ai-test`;

  // Helper function to make API calls
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('access_token');
    console.log("🔐 AdminAITest - Token found:", !!token);
    console.log("🔐 AdminAITest - API URL:", `${API_BASE}${endpoint}`);
    const response = await fetch(`${API_BASE}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // =====================================================
  // Prompt Library Functions
  // =====================================================

  // Fetch prompt templates from library on component mount
  const fetchPromptLibrary = async () => {
    try {
      setPromptLoading(true);
      const response = await apiCall('/prompt-library');
      if (response.success) {
        setPromptLibrary(response.prompts || []);
        console.log(`📚 Loaded ${response.count} prompts from library`);
      }
    } catch (err) {
      console.error('Failed to load prompt library:', err);
      // Don't show error to user - prompt library is optional
    } finally {
      setPromptLoading(false);
    }
  };

  // Fetch master data (ethnicities, clothing types) for dropdowns
  const fetchMasterData = async () => {
    try {
      const response = await apiCall('/master-data');
      if (response.success) {
        setMasterData({
          ethnicities: response.ethnicities || [],
          clothing_types: response.clothing_types || [],
          bust_finishes: response.bust_finishes || [],
          bust_colors: response.bust_colors || []
        });
        console.log(`📊 Loaded ${response.ethnicities?.length || 0} ethnicities, ${response.clothing_types?.length || 0} clothing types, ${response.bust_finishes?.length || 0} bust finishes, ${response.bust_colors?.length || 0} bust colors`);
      }
    } catch (err) {
      console.error('Failed to load master data:', err);
    }
  };

  // Handle prompt selection from dropdown
  const handlePromptSelection = async (promptId) => {
    if (!promptId) {
      // Clear selection
      setSelectedPromptId(null);
      setProcessedPrompt(null);
      setReplacedVariables([]);
      return;
    }

    setSelectedPromptId(promptId);
    const selectedPrompt = promptLibrary.find(p => p.id === parseInt(promptId));

    if (selectedPrompt) {
      const isBustModel = selectedPrompt.model_type_name === 'Bust Display' || selectedPrompt.model_type_id === 22;

      // Store randomized values in variables to avoid async state timing issues
      let randomizedBustFinishId = null;
      let randomizedBustColorId = null;

      // Initial randomization logic
      if (isBustModel) {
        // If BUST model, randomize bust finish and color if not fixed by template
        if (masterData.bust_finishes.length > 0) {
          const randomFinish = masterData.bust_finishes[Math.floor(Math.random() * masterData.bust_finishes.length)];
          randomizedBustFinishId = randomFinish.id;
          setSelectedBustFinish(randomFinish.id);
        }
        if (masterData.bust_colors.length > 0) {
          const randomColor = masterData.bust_colors[Math.floor(Math.random() * masterData.bust_colors.length)];
          randomizedBustColorId = randomColor.id;
          setSelectedBustColor(randomColor.id);
        }
        // Clear human attributes
        setSelectedEthnicity('');
        setSelectedClothingType('');
      } else {
        // If HUMAN_MODEL or others, randomize ethnicity and clothing if not fixed
        // Note: Current behavior randomizes by default (empty value means randomized on backend)
        setSelectedEthnicity('');
        setSelectedClothingType('');
        // Clear bust attributes
        setSelectedBustFinish('');
        setSelectedBustColor('');
      }

      // Update form data with selected prompt settings
      setFormData(prev => ({
        ...prev,
        // For video mode, populate image_prompt_1; for image mode, populate prompt
        ...(generationType === 'video'
          ? { image_prompt_1: selectedPrompt.prompt_template }
          : { prompt: selectedPrompt.prompt_template }
        ),
        guidance_scale: selectedPrompt.guidance_scale || prev.guidance_scale,
        ratio: selectedPrompt.aspect_ratio || '1:1',
        resolution: selectedPrompt.resolution || '2k'
      }));

      // Process the prompt to show variable replacements
      // Use the NEWLY randomized values (not stale state) to ensure sync with dropdowns
      const userAttributesOverride = isBustModel
        ? { bust_finish_id: randomizedBustFinishId, bust_color_id: randomizedBustColorId }
        : { ethnicity_id: '', clothing_type_id: '' };

      await processPromptWithVariables(
        selectedPrompt.prompt_template,
        selectedPrompt.dictionary_data,
        parseInt(promptId),
        userAttributesOverride
      );

      // If in video mode, also fetch and populate the VIDEO companion prompt
      if (generationType === 'video' && selectedPrompt.category_id) {
        await refreshVideoCompanion(selectedPrompt.category_id, selectedPrompt.model_type_id);
      }
    }
  };

  // Helper to fetch and process video companion prompt
  const refreshVideoCompanion = async (categoryId, modelTypeId) => {
    try {
      setPromptLoading(true);
      const data = await apiCall(`/prompt-library/video-companion/${categoryId}?model_type_id=${modelTypeId}`);

      if (data.success && data.prompt) {
        // Build user_attributes for video prompt processing
        const userAttributes = {};
        if (selectedEthnicity) userAttributes.ethnicity_id = parseInt(selectedEthnicity);
        if (selectedClothingType) userAttributes.clothing_type_id = parseInt(selectedClothingType);
        if (selectedBustFinish) userAttributes.bust_finish_id = parseInt(selectedBustFinish);
        if (selectedBustColor) userAttributes.bust_color_id = parseInt(selectedBustColor);

        // Determine the template to use (fallback to random Variation if prompt_template is empty)
        let effectiveTemplate = data.prompt.prompt_template;
        if (!effectiveTemplate || !effectiveTemplate.trim()) {
          const variations = data.prompt.dictionary_data?.Variations;
          if (Array.isArray(variations) && variations.length > 0) {
            const randomIndex = Math.floor(Math.random() * variations.length);
            effectiveTemplate = variations[randomIndex];
            console.log(`ℹ️ Using random Variation [${randomIndex + 1}/${variations.length}] as fallback for empty video template`);
          }
        }

        // Process the video prompt with variables ONLY if we have a template
        if (effectiveTemplate && effectiveTemplate.trim()) {
          const videoPromptData = await apiCall('/process-prompt', {
            method: 'POST',
            body: JSON.stringify({
              prompt_template: effectiveTemplate,
              user_attributes: userAttributes,
              dictionary_data: data.prompt.dictionary_data
            })
          });

          if (videoPromptData.success && videoPromptData.processed_prompt) {
            // Update formData with the processed video prompt
            setFormData(prev => ({
              ...prev,
              video_prompt: videoPromptData.processed_prompt
            }));

            console.log('✅ Auto-filled video prompt from companion template');
          }
        } else {
          // If template is empty, just clear the field
          setFormData(prev => ({
            ...prev,
            video_prompt: ''
          }));
          console.log('ℹ️ Video companion template is empty, clearing video prompt');
        }
      }
    } catch (error) {
      console.error('⚠️ Failed to refresh video companion prompt:', error);
    } finally {
      setPromptLoading(false);
    }
  };


  // Process prompt template with variable replacement
  const processPromptWithVariables = async (template, dictionaryData, promptRegistryId, userAttributesOverride = null) => {
    if (!template) {
      setProcessedPrompt(null);
      setReplacedVariables([]);
      return;
    }

    try {
      setPromptLoading(true);

      // Build user_attributes from selected ethnicity and clothing type
      // Use override values if provided (useful for immediate processing on select change)
      const userAttributes = {};

      const ethnicityId = userAttributesOverride?.ethnicity_id !== undefined
        ? userAttributesOverride.ethnicity_id
        : selectedEthnicity;

      const clothingTypeId = userAttributesOverride?.clothing_type_id !== undefined
        ? userAttributesOverride.clothing_type_id
        : selectedClothingType;

      const bustFinishId = userAttributesOverride?.bust_finish_id !== undefined
        ? userAttributesOverride.bust_finish_id
        : selectedBustFinish;

      const bustColorId = userAttributesOverride?.bust_color_id !== undefined
        ? userAttributesOverride.bust_color_id
        : selectedBustColor;

      if (ethnicityId) {
        userAttributes.ethnicity_id = parseInt(ethnicityId);
      }
      if (clothingTypeId) {
        userAttributes.clothing_type_id = parseInt(clothingTypeId);
      }
      if (bustFinishId) {
        userAttributes.bust_finish_id = parseInt(bustFinishId);
      }
      if (bustColorId) {
        userAttributes.bust_color_id = parseInt(bustColorId);
      }

      const response = await apiCall('/process-prompt', {
        method: 'POST',
        body: JSON.stringify({
          prompt_template: template,
          dictionary_data: dictionaryData,
          prompt_registry_id: promptRegistryId,
          user_attributes: userAttributes  // Pass selected ethnicity/clothing type
        })
      });

      if (response.success) {
        setProcessedPrompt(response.processed_prompt);
        setReplacedVariables(response.replaced_variables || []);
        // Update the prompt field with the processed version
        setFormData(prev => ({
          ...prev,
          ...(generationType === 'video'
            ? { image_prompt_1: response.processed_prompt || template }
            : { prompt: response.processed_prompt || template }
          )
        }));
      }
    } catch (err) {
      console.error('Failed to process prompt:', err);
      // Fall back to using the original template
      setProcessedPrompt(template);
      setReplacedVariables([]);
    } finally {
      setPromptLoading(false);
    }
  };

  // Refresh prompt with new random variable values
  const handleRefreshPrompt = async () => {
    if (!selectedPromptId) return;

    const selectedPrompt = promptLibrary.find(p => p.id === parseInt(selectedPromptId));
    if (selectedPrompt) {
      // 1. Refresh Step 1 (Image) prompt
      await processPromptWithVariables(
        selectedPrompt.prompt_template,
        selectedPrompt.dictionary_data,
        parseInt(selectedPromptId)
      );

      // 2. Refresh Step 2 (Video) prompt if in video mode
      if (generationType === 'video' && selectedPrompt.category_id) {
        await refreshVideoCompanion(selectedPrompt.category_id, selectedPrompt.model_type_id);
      }

      setSuccess('✅ Prompts refreshed with new variable values!');
    }
  };

  // Render prompt text with replaced variables highlighted in bold
  // Render prompt text with replaced variables highlighted in bold
  const renderPromptWithHighlights = () => {
    if (!processedPrompt || replacedVariables.length === 0) {
      return <span>{formData.prompt || processedPrompt}</span>;
    }

    // Sort variables by index to process them in linear order
    // Ensure we handle variables without index (fall back to -1)
    const sortedVariables = [...replacedVariables]
      .filter(v => v.replacement && !v.replacement.startsWith('[replaced:'))
      .sort((a, b) => (a.index || 0) - (b.index || 0));

    if (sortedVariables.length === 0) {
      return <span>{processedPrompt}</span>;
    }

    const parts = [];
    let currentPos = 0;
    let keyIndex = 0;

    sortedVariables.forEach((variable) => {
      const { index, replacement, variable: varName } = variable;

      // If we have a valid index and it's at or after our current position
      if (index !== undefined && index >= currentPos) {
        // Add text before this variable
        if (index > currentPos) {
          parts.push(
            <span key={`text-${keyIndex++}`}>
              {processedPrompt.substring(currentPos, index)}
            </span>
          );
        }

        // Add the highlighted variable
        parts.push(
          <strong
            key={`bold-${keyIndex++}`}
            className="variable-highlight"
            title={`Variable: {{${varName}}}`}
          >
            {replacement}
          </strong>
        );

        // Move position past this replacement
        currentPos = index + replacement.length;
      }
    });

    // Add any remaining text after the last variable
    if (currentPos < processedPrompt.length) {
      parts.push(
        <span key={`text-${keyIndex++}`}>
          {processedPrompt.substring(currentPos)}
        </span>
      );
    }

    return parts.length > 0 ? parts : <span>{processedPrompt}</span>;
  };

  // Validate folder name
  const validateFolderName = (folderName) => {
    if (!folderName.trim()) {
      return 'Folder name is required';
    }

    // Check for valid characters (alphanumeric, hyphens, underscores, spaces)
    const validPattern = /^[a-zA-Z0-9\s\-_]+$/;
    if (!validPattern.test(folderName)) {
      return 'Folder name can only contain letters, numbers, spaces, hyphens, and underscores';
    }

    // Check length
    if (folderName.length < 3) {
      return 'Folder name must be at least 3 characters long';
    }

    if (folderName.length > 50) {
      return 'Folder name must be less than 50 characters';
    }

    // Check for consecutive spaces or special characters
    if (folderName.includes('  ') || folderName.startsWith(' ') || folderName.endsWith(' ')) {
      return 'Folder name cannot have leading/trailing spaces or consecutive spaces';
    }

    return null; // Valid
  };

  // Handle generation type change
  const handleGenerationTypeChange = (type) => {
    setGenerationType(type);
    setFormData(prev => ({
      ...prev,
      output_format: type
    }));
    // Clear API testing status when switching types
    setApiTestingStatus({
      step1: { testing: false, success: false, error: '' },
      step2: { testing: false, success: false, error: '' }
    });
  };

  // Test API connection
  const testApiConnection = async (step) => {
    const isStep1 = step === 'step1';
    const apiUrl = isStep1 ? formData.step1_api_url : formData.step2_api_url;
    const apiKey = isStep1 ? formData.step1_api_key : formData.step2_api_key;
    const model = isStep1 ? formData.step1_model : formData.step2_model;

    if (!apiUrl.trim()) {
      setApiTestingStatus(prev => ({
        ...prev,
        [step]: { testing: false, success: false, error: 'API URL is required' }
      }));
      return;
    }

    setApiTestingStatus(prev => ({
      ...prev,
      [step]: { testing: true, success: false, error: '' }
    }));

    try {
      const response = await apiCall('/test-api-connection', {
        method: 'POST',
        body: JSON.stringify({
          api_url: apiUrl,
          api_key: apiKey,
          model: model,
          step: step
        })
      });

      setApiTestingStatus(prev => ({
        ...prev,
        [step]: { testing: false, success: true, error: '' }
      }));

    } catch (err) {
      setApiTestingStatus(prev => ({
        ...prev,
        [step]: { testing: false, success: false, error: err.message }
      }));
    }
  };

  // Fetch S3 images
  const handleFetchS3Images = async () => {
    // For use_existing mode, use selected folder's S3 path, otherwise use form data
    const s3Path = uploadMode === 'use_existing' && selectedFolder
      ? selectedFolder.s3_folder_path
      : formData.s3_folder_path;

    if (!s3Path.trim()) {
      setError('Please select a folder or enter an S3 folder path');
      return;
    }

    setS3Loading(true);
    setError('');
    setInfo('');

    try {
      const response = await apiCall('/fetch-s3-images', {
        method: 'POST',
        body: JSON.stringify({ s3_folder_path: s3Path })
      });

      setS3Images(response.images || []);
      setInfo(`Found ${response.count} images in folder: ${response.folder_path}`);
    } catch (err) {
      setError(`Failed to fetch S3 images: ${err.message}`);
      setS3Images([]);
    } finally {
      setS3Loading(false);
    }
  };

  // Handle folder upload
  const handleFolderUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Validate files
    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      if (file.type.startsWith('image/')) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: Not an image file`);
      }
    });

    if (errors.length > 0) {
      setError(`Invalid files: ${errors.join(', ')}`);
    }

    setUploadedFiles(validFiles);

    // For create new mode, enable folder upload mode
    // For append mode, just show the files without changing folder upload mode
    if (uploadMode === 'create_new') {
      setFolderUploadMode(true);
    }

    setError('');
  };

  // Upload folder to S3
  const handleUploadToS3 = async () => {
    if (uploadedFiles.length === 0) {
      setError('No files selected for upload');
      return;
    }

    if (uploadMode === 'append_existing' && !selectedFolder) {
      setError('Please select a folder to append images to');
      return;
    }

    // Validate folder name for create_new mode
    if (uploadMode === 'create_new') {
      const folderNameError = validateFolderName(formData.folder_name);
      if (folderNameError) {
        setError(folderNameError);
        return;
      }
    }

    setIsUploading(true);
    setError('');
    setInfo('');

    try {
      const formDataToSend = new FormData();
      uploadedFiles.forEach((file) => {
        formDataToSend.append('files', file);
      });

      // Add upload mode and folder path
      formDataToSend.append('upload_mode', uploadMode);
      if (uploadMode === 'append_existing' && selectedFolder) {
        formDataToSend.append('existing_folder_path', selectedFolder.s3_folder_path);
      } else if (uploadMode === 'create_new') {
        // Add user-defined folder name
        formDataToSend.append('folder_name', formData.folder_name.trim());
      }

      const response = await fetch(`${API_BASE}/upload-folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Update S3 folder path and fetch images
      setFormData(prev => ({
        ...prev,
        s3_folder_path: data.folder_path
      }));

      const modeText = data.mode === 'append_existing' ? 'appended to existing folder' : 'uploaded to new folder';
      setInfo(`Successfully ${modeText} ${data.count} files. Fetching images...`);

      // Automatically fetch the uploaded images
      await handleFetchS3Images();

      // Reset upload state
      setFolderUploadMode(false);
      setUploadedFiles([]);
      setSelectedFolder(null);
      setUploadMode('create_new');
      setFormData(prev => ({ ...prev, folder_name: '' })); // Clear folder name

      // Reload folders to update metadata
      await loadExistingFolders();

    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Cancel folder upload
  const handleCancelUpload = () => {
    setFolderUploadMode(false);
    setUploadedFiles([]);
    setUploadProgress({});
    setError('');
  };

  // Load existing folders
  const loadExistingFolders = async () => {
    setFoldersLoading(true);
    try {
      const response = await apiCall('/list-folders');
      setExistingFolders(response.folders || []);
    } catch (err) {
      setError(`Failed to load folders: ${err.message}`);
    } finally {
      setFoldersLoading(false);
    }
  };

  // Handle folder selection
  const handleFolderSelection = (folder) => {
    setSelectedFolder(folder);
    setFormData(prev => ({ ...prev, s3_folder_path: folder.s3_folder_path }));
    setShowFolderBrowser(false);
  };

  // Handle upload mode change
  const handleUploadModeChange = (mode) => {
    setUploadMode(mode);
    if (mode === 'create_new') {
      setSelectedFolder(null);
      setFormData(prev => ({ ...prev, s3_folder_path: '' }));
    } else if (mode === 'use_existing') {
      setSelectedFolder(null);
      setFormData(prev => ({ ...prev, s3_folder_path: '' }));
      setShowFolderBrowser(true);
      loadExistingFolders();
    }
    // Reset folder upload mode when switching modes
    setFolderUploadMode(false);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString();
  };

  // Format date and time in India timezone
  const formatDateTimeIndia = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  // Helper functions for state persistence
  const saveBatchState = (batchId, status, results) => {
    if (batchId) {
      localStorage.setItem('admin_ai_test_current_batch', JSON.stringify(batchId));
    }
    if (status) {
      localStorage.setItem('admin_ai_test_batch_status', JSON.stringify(status));
    }
    if (results) {
      localStorage.setItem('admin_ai_test_batch_results', JSON.stringify(results));
    }
  };

  const clearBatchState = () => {
    localStorage.removeItem('admin_ai_test_current_batch');
    localStorage.removeItem('admin_ai_test_batch_status');
    localStorage.removeItem('admin_ai_test_batch_results');
  };

  // Manual clear batch function for user
  const handleClearBatch = () => {
    clearBatchState();
    setCurrentBatch(null);
    setBatchStatus(null);
    setBatchResults([]);
    setSuccess('Batch cleared successfully');
  };

  // Start generation
  const handleStartGeneration = async () => {
    if (!canCreate) {
      setError('You don\'t have permission to create test generations');
      return;
    }

    // Validation based on generation type
    if (generationType === 'image') {
      // Single API validation for image generation
      if (!formData.api_url.trim()) {
        setError('API URL is required for image generation');
        return;
      }
      if (!formData.prompt.trim()) {
        setError('Prompt is required for image generation');
        return;
      }
    } else {
      // Dual API validation for video generation
      if (!formData.step1_api_url.trim()) {
        setError('Step 1 API URL is required for video generation');
        return;
      }
      if (!formData.step2_api_url.trim()) {
        setError('Step 2 API URL is required for video generation');
        return;
      }
      if (!formData.image_prompt_1.trim() || !formData.video_prompt.trim()) {
        setError('Both image prompt and video prompt are required for video generation');
        return;
      }
    }

    if (!formData.s3_folder_path.trim()) {
      setError('S3 folder path is required');
      return;
    }

    if (s3Images.length === 0) {
      setError('Please fetch S3 images first');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiCall('/generate', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      setCurrentBatch(response.batch_id);
      setSuccess(`Started ${response.output_format} generation for ${response.image_count} images. Batch ID: ${response.batch_id}`);

      // Save batch state to localStorage
      saveBatchState(response.batch_id, null, []);

      // Switch to results tab
      setActiveTab('results');

      // Start polling for batch status (will show queued status initially)
      pollBatchStatus(response.batch_id);

    } catch (err) {
      setError(`Failed to start generation: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Poll batch status
  const pollBatchStatus = async (batchId) => {
    try {
      const response = await apiCall(`/batch-status/${batchId}`);
      setBatchStatus(response.statistics);
      setBatchResults(response.generations || []);

      // Save updated state to localStorage
      saveBatchState(batchId, response.statistics, response.generations || []);

      // Continue polling if batch is not complete
      if (response.statistics.pending > 0 || response.statistics.processing > 0) {
        setTimeout(() => pollBatchStatus(batchId), 5000); // Poll every 5 seconds
      } else {
        // Batch is complete, clear state after a delay to allow user to see final results
        setTimeout(() => {
          clearBatchState();
          setCurrentBatch(null);
          setBatchStatus(null);
          setBatchResults([]);
        }, 30000); // Clear after 30 seconds
      }
    } catch (err) {
      console.error('Error polling batch status:', err);
    }
  };

  // Load history - using same pagination pattern as FeedbackManagement.js
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      // Format date filters for backend - send date only (YYYY-MM-DD format)
      const formattedFilters = { ...historyFilters };
      // No need to modify date_from and date_to as they are already in YYYY-MM-DD format

      const params = new URLSearchParams({
        page: (page + 1).toString(), // Backend expects 1-based page numbers
        per_page: rowsPerPage.toString(),
        ...Object.fromEntries(
          Object.entries(formattedFilters).filter(([_, value]) => value.trim() !== '')
        )
      });

      const response = await apiCall(`/history?${params}`);
      setHistory(response.history || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (err) {
      setError(`Failed to load history: ${err.message}`);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handle page change - same pattern as FeedbackManagement.js
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Load specific page when pagination changes
    loadHistory();
  };

  // Handle rows per page change - same pattern as FeedbackManagement.js
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    // Load first page when rows per page changes
    loadHistory();
  };

  // Load history on tab change, filter change, or pagination change
  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, historyFilters, page, rowsPerPage]);

  // Reset to first page when filters change
  useEffect(() => {
    if (activeTab === 'history') {
      setPage(0);
    }
  }, [historyFilters]);

  // Recovery logic: Check for active batch on component mount
  useEffect(() => {
    const recoverActiveBatch = async () => {
      if (currentBatch && batchStatus) {
        // Check if batch is still active (has pending or processing items)
        if (batchStatus.pending > 0 || batchStatus.processing > 0) {
          console.log(`🔄 Recovering active batch: ${currentBatch}`);
          // Resume polling for the active batch
          pollBatchStatus(currentBatch);
        } else {
          // Batch is complete, clear state
          console.log(`✅ Batch ${currentBatch} is complete, clearing state`);
          clearBatchState();
          setCurrentBatch(null);
          setBatchStatus(null);
          setBatchResults([]);
        }
      }
    };

    recoverActiveBatch();
  }, []); // Run only on component mount

  // Clear messages after timeout
  useEffect(() => {
    if (error || success || info) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
        setInfo('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error, success, info]);

  // Load prompt library and master data on component mount
  useEffect(() => {
    fetchPromptLibrary();
    fetchMasterData();
  }, []); // Run only on component mount

  if (!canView) {
    return (
      <div className="admin-ai-test">
        <div className="error-message">
          You don't have permission to view the AI Test Module.
        </div>
      </div>
    );
  }

  // Handle view details
  const handleViewDetails = (item) => {
    console.log('Selected history item:', item);
    console.log('Generated image path:', item.generated_image_path);
    console.log('Generated video path:', item.generated_video_path);
    console.log('Output format:', item.output_format);
    setSelectedHistoryItem(item);
    setImageLoadErrors({}); // Clear previous image load errors
    setDetailsDialogOpen(true);
  };

  // Close details dialog
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedHistoryItem(null);
    setCollageMode(false);
    setZoomLevel(0.5);
    setCollageImagesLoaded({ input: false, output: false });
  };

  // Handle collage mode toggle
  const handleCollageModeToggle = () => {
    setCollageMode(!collageMode);
    setZoomLevel(0.5);
  };

  // Handle zoom
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleZoomReset = () => {
    setZoomLevel(0.5);
  };

  // Handle collage image load
  const handleCollageImageLoad = (type) => {
    setCollageImagesLoaded(prev => ({ ...prev, [type]: true }));
  };

  // Download collage as image
  const handleDownloadCollage = async () => {
    if (!selectedHistoryItem) return;

    try {
      // Get image URLs
      const inputImageUrl = getSecureAdminAITestUrl(
        selectedHistoryItem.original_image_path || selectedHistoryItem.input_image_path,
        selectedHistoryItem.id,
        true
      );

      let outputImageUrl = null;
      if (selectedHistoryItem.output_format === 'video') {
        outputImageUrl = getSecureAdminAITestUrl(
          selectedHistoryItem.generated_video_path || selectedHistoryItem.output_video_path,
          selectedHistoryItem.id
        );
      } else {
        outputImageUrl = getSecureAdminAITestUrl(
          selectedHistoryItem.generated_image_path || selectedHistoryItem.output_image_path,
          selectedHistoryItem.id
        );
      }

      if (!inputImageUrl || !outputImageUrl) {
        setError('Unable to download collage: images not available');
        return;
      }

      // Load images - ensure we get exact S3 dimensions
      const loadImage = (url) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            // Verify we have natural dimensions (actual S3 size)
            if (img.naturalWidth === 0 || img.naturalHeight === 0) {
              reject(new Error('Image natural dimensions are zero - may be blocked by CORS or invalid'));
            }
            resolve(img);
          };
          img.onerror = (err) => {
            console.error('Failed to load image:', url, err);
            reject(err);
          };
          img.src = url;
        });
      };

      const [inputImg, outputImg] = await Promise.all([
        loadImage(inputImageUrl),
        loadImage(outputImageUrl)
      ]);

      // Create canvas for collage - use ORIGINAL image dimensions from S3
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Use naturalWidth/naturalHeight to get ACTUAL pixel dimensions stored in S3 (not display dimensions)
      const inputWidth = inputImg.naturalWidth;
      const inputHeight = inputImg.naturalHeight;
      const outputWidth = outputImg.naturalWidth;
      const outputHeight = outputImg.naturalHeight;

      // Debug: Log actual dimensions to verify we're getting S3 original size
      console.log('📸 Actual S3 image dimensions:', {
        input: { width: inputWidth, height: inputHeight },
        output: { width: outputWidth, height: outputHeight }
      });

      // Calculate canvas dimensions - enough space for both images side by side
      const spacing = 60; // Space between images
      const labelHeight = 60; // Space for labels at top
      const maxHeight = Math.max(inputHeight, outputHeight);

      canvas.width = inputWidth + outputWidth + spacing + 40; // 20px margin on each side
      canvas.height = maxHeight + labelHeight + 40; // 20px margin top and bottom

      // Disable image smoothing for pixel-perfect rendering from S3
      // Smoothing can blur/soften edges and reduce quality
      ctx.imageSmoothingEnabled = false;

      // Fill background
      ctx.fillStyle = '#f8f9fa';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw input image at ORIGINAL size
      ctx.drawImage(inputImg, 20, 60, inputWidth, inputHeight);

      // Draw output image at ORIGINAL size
      ctx.drawImage(outputImg, inputWidth + spacing, 60, outputWidth, outputHeight);

      // Add labels
      ctx.fillStyle = '#333';
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('Input Image', 20, 45);
      ctx.fillText('Output', inputWidth + spacing, 45);

      // Download in PNG format for TRUE lossless quality - preserves exact pixel data from S3
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ai-test-collage-${selectedHistoryItem.id}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Log file size to verify quality preservation
        const fileSizeMB = (blob.size / (1024 * 1024)).toFixed(2);
        console.log(`📊 Collage file size: ${fileSizeMB} MB (${(blob.size / 1024).toFixed(0)} KB)`);
        setSuccess(`Collage downloaded successfully at original S3 quality! (${(blob.size / 1024).toFixed(0)} KB)`);
      }, 'image/png'); // PNG = lossless, preserves original S3 quality

    } catch (err) {
      console.error('Error downloading collage:', err);
      setError('Failed to download collage: ' + err.message);
    }
  };

  // Handle image click
  const handleImageClick = (imageUrl, title) => {
    setSelectedImage({ url: imageUrl, title });
    setImageDialogOpen(true);
  };

  // Close image dialog
  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setSelectedImage(null);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED': return 'success';
      case 'FAILED': return 'error';
      case 'PROCESSING': return 'warning';
      case 'PENDING': return 'default';
      default: return 'default';
    }
  };

  // Handle image load error
  const handleImageError = (imageUrl, imageType) => {
    console.error(`${imageType} image failed to load:`, imageUrl);
    setImageLoadErrors(prev => ({
      ...prev,
      [imageUrl]: true
    }));
  };

  // Render S3 images preview
  const renderS3Preview = () => {
    if (s3Loading) {
      return (
        <div className="loading">
          <div className="spinner"></div>
          Loading images...
        </div>
      );
    }

    if (s3Images.length === 0) {
      return <div style={{ color: '#666', fontStyle: 'italic' }}>No images loaded. Click "Fetch S3 Images" to load images from the folder.</div>;
    }

    return (
      <div className="s3-images-grid">
        {s3Images.map((image, index) => (
          <div key={index} className="s3-image-item">
            <img src={image.preview_url || image.url} alt={image.filename} />
            <div className="filename">{image.filename}</div>
          </div>
        ))}
      </div>
    );
  };

  // Render batch status
  const renderBatchStatus = () => {
    if (!batchStatus) {
      return <div className="info-message">No active batch. Start a new generation to see results here.</div>;
    }

    const completionPercentage = batchStatus.completion_percentage || 0;
    const isQueued = batchStatus.status === 'queued' || (batchStatus.processing === 0 && batchStatus.pending > 0);

    return (
      <div className="batch-status">
        <h3>Batch Status: {currentBatch}</h3>

        <div className="status-grid">
          <div className="status-item status-completed">
            <div className="status-number">{batchStatus.completed}</div>
            <div className="status-label">Completed</div>
          </div>
          <div className="status-item status-failed">
            <div className="status-number">{batchStatus.failed}</div>
            <div className="status-label">Failed</div>
          </div>
          <div className="status-item status-processing">
            <div className="status-number">{batchStatus.processing}</div>
            <div className="status-label">Processing</div>
          </div>
          <div className="status-item status-pending">
            <div className="status-number">{batchStatus.pending}</div>
            <div className="status-label">Pending</div>
          </div>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '14px', color: '#666' }}>
          {completionPercentage.toFixed(1)}% Complete
        </div>

      </div>
    );
  };

  // Render generation results
  const renderGenerationResults = () => {
    if (batchResults.length === 0) {
      return <div className="info-message">No results yet. Results will appear here as generations complete.</div>;
    }

    return (
      <div className="generation-results">
        {batchResults.map((result) => {
          const progressPercentage = getProgressPercentage(result.status);
          const isCompleted = result.status === 'COMPLETED';
          const isFailed = result.status === 'FAILED';
          const isProcessing = result.status === 'PROCESSING';
          const isPending = result.status === 'PENDING';

          return (
            <div key={result.id} className="generation-card">
              {/* Card Header with Status and Progress */}
              <div className="card-header">
                <div className="status-section">
                  <div className={`status-badge ${result.status.toLowerCase()}`}>
                    {result.status}
                  </div>
                  <div className="generation-id">#{result.id}</div>
                </div>
                <div className="progress-circle-container">
                  <div className="progress-circle">
                    <svg className="progress-ring" width="40" height="40">
                      <circle
                        className="progress-ring-circle"
                        stroke="#e2e8f0"
                        strokeWidth="3"
                        fill="transparent"
                        r="16"
                        cx="20"
                        cy="20"
                      />
                      <circle
                        className="progress-ring-circle progress-ring-fill"
                        stroke={isFailed ? "#ef4444" : isCompleted ? "#10b981" : "#4f46e5"}
                        strokeWidth="3"
                        fill="transparent"
                        r="16"
                        cx="20"
                        cy="20"
                        style={{
                          strokeDasharray: `${2 * Math.PI * 16}`,
                          strokeDashoffset: `${2 * Math.PI * 16 * (1 - progressPercentage / 100)}`
                        }}
                      />
                    </svg>
                    <div className="progress-text">{progressPercentage}%</div>
                  </div>
                </div>
              </div>

              {/* Images Section */}
              <div className="images-section">
                {/* Input Image */}
                <div className="image-container">
                  <div className="image-label">Input</div>
                  <div className="image-wrapper">
                    {(result.original_image_path || result.input_image_path) ? (
                      imageLoadErrors[result.original_image_path || result.input_image_path] ? (
                        <div className="no-image-placeholder">
                          <span>Input image failed to load</span>
                        </div>
                      ) : (
                        <SecureImage
                          src={getSecureAdminAITestUrl(result.original_image_path || result.input_image_path, result.id, true)}
                          alt="Input"
                          className="card-image"
                          onError={() => handleImageError(result.original_image_path || result.input_image_path, 'Input')}
                          onClick={() => handleImageClick(
                            getSecureAdminAITestUrl(result.original_image_path || result.input_image_path, result.id, true),
                            'Input Image'
                          )}
                        />
                      )
                    ) : (
                      <div className="no-image-placeholder">
                        <span>No input image</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Generated Output */}
                <div className="image-container">
                  <div className="image-label">Output</div>
                  <div className="image-wrapper">
                    {result.generated_video_path ? (
                      imageLoadErrors[result.generated_video_path] ? (
                        <div className="no-image-placeholder">
                          <span>Video failed to load</span>
                        </div>
                      ) : (
                        <SecureVideo
                          src={getSecureAdminAITestUrl(result.generated_video_path, result.id)}
                          className="card-image card-video"
                          controls
                          onError={() => handleImageError(result.generated_video_path, 'Video')}
                          onClick={() => handleImageClick(
                            getSecureAdminAITestUrl(result.generated_video_path, result.id),
                            'Generated Video'
                          )}
                        />
                      )
                    ) : result.generated_image_path ? (
                      imageLoadErrors[result.generated_image_path] ? (
                        <div className="no-image-placeholder">
                          <span>Image failed to load</span>
                        </div>
                      ) : (
                        <SecureImage
                          src={getSecureAdminAITestUrl(result.generated_image_path, result.id)}
                          alt="Generated"
                          className="card-image"
                          onError={() => handleImageError(result.generated_image_path, 'Generated')}
                          onClick={() => handleImageClick(
                            getSecureAdminAITestUrl(result.generated_image_path, result.id),
                            'Generated Image'
                          )}
                        />
                      )
                    ) : (
                      <div className="no-image-placeholder">
                        {isProcessing ? (
                          <div className="processing-indicator">
                            <div className="spinner"></div>
                            <span>Generating...</span>
                          </div>
                        ) : isPending ? (
                          <span>Waiting...</span>
                        ) : isFailed ? (
                          <span>Failed</span>
                        ) : (
                          <span>No output</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Card Footer with Details */}
              <div className="card-footer">
                {result.processing_time_seconds && (
                  <div className="detail-item">
                    <span className="detail-label">Time:</span>
                    <span className="detail-value">{result.processing_time_seconds}s</span>
                  </div>
                )}
                {result.error_message && (
                  <div className="detail-item error">
                    <span className="detail-label">Error:</span>
                    <span className="detail-value">{result.error_message}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Original:</span>
                  <span className="detail-value filename">
                    {(result.original_image_path || result.input_image_path) ?
                      (result.original_image_path || result.input_image_path).split('/').pop() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Helper function to get progress percentage based on status
  const getProgressPercentage = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED': return 100;
      case 'FAILED': return 100;
      case 'PROCESSING': return 75;
      case 'PENDING': return 25;
      default: return 0;
    }
  };

  // Render history table - using same pattern as FeedbackManagement.js
  const renderHistoryTable = () => {
    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Format</TableCell>
                <TableCell>Input</TableCell>
                <TableCell>Output</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      <Typography variant="body2">Loading history...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No test history found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                history.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>
                      <span className={`status-badge ${item.status.toLowerCase()}`}>
                        {item.status}
                      </span>
                    </TableCell>
                    <TableCell>{item.output_format}</TableCell>
                    <TableCell>
                      {(item.original_image_path || item.input_image_path) ? (
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            cursor: 'pointer',
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: '1px solid #e0e0e0',
                            '&:hover': { borderColor: '#1976d2' }
                          }}
                          onClick={() => handleImageClick(
                            getSecureAdminAITestUrl(item.original_image_path || item.input_image_path, item.id, true),
                            'Input Image'
                          )}
                        >
                          {imageLoadErrors[item.original_image_path || item.input_image_path] ? (
                            <Box sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'grey.100',
                              color: 'text.secondary',
                              fontSize: '0.75rem'
                            }}>
                              Failed to load
                            </Box>
                          ) : (
                            <SecureImage
                              src={getSecureAdminAITestUrl(item.original_image_path || item.input_image_path, item.id, true)}
                              alt="Input"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={() => handleImageError(item.original_image_path || item.input_image_path, 'Input')}
                            />
                          )}
                        </Box>
                      ) : (
                        <Box sx={{
                          width: 60,
                          height: 60,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.100',
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          borderRadius: 1,
                          border: '1px dashed #e0e0e0'
                        }}>
                          No input
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.generated_video_path ? (
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            cursor: 'pointer',
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: '1px solid #e0e0e0',
                            '&:hover': { borderColor: '#1976d2' }
                          }}
                          onClick={() => handleImageClick(
                            getSecureAdminAITestUrl(item.generated_video_path, item.id),
                            'Generated Video'
                          )}
                        >
                          {imageLoadErrors[item.generated_video_path] ? (
                            <Box sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'grey.100',
                              color: 'text.secondary',
                              fontSize: '0.75rem'
                            }}>
                              Video failed to load
                            </Box>
                          ) : (
                            <SecureVideo
                              src={getSecureAdminAITestUrl(item.generated_video_path, item.id)}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={() => handleImageError(item.generated_video_path, 'Video')}
                            />
                          )}
                        </Box>
                      ) : item.generated_image_path ? (
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            cursor: 'pointer',
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: '1px solid #e0e0e0',
                            '&:hover': { borderColor: '#1976d2' }
                          }}
                          onClick={() => handleImageClick(
                            getSecureAdminAITestUrl(item.generated_image_path, item.id),
                            'Generated Image'
                          )}
                        >
                          {imageLoadErrors[item.generated_image_path] ? (
                            <Box sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'grey.100',
                              color: 'text.secondary',
                              fontSize: '0.75rem'
                            }}>
                              Image failed to load
                            </Box>
                          ) : (
                            <SecureImage
                              src={getSecureAdminAITestUrl(item.generated_image_path, item.id)}
                              alt="Generated"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={() => handleImageError(item.generated_image_path, 'Generated')}
                            />
                          )}
                        </Box>
                      ) : (
                        <Box sx={{
                          width: 60,
                          height: 60,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.100',
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          borderRadius: 1,
                          border: '1px dashed #e0e0e0'
                        }}>
                          {item.status === 'PROCESSING' ? 'Generating...' :
                            item.status === 'PENDING' ? 'Waiting...' :
                              item.status === 'FAILED' ? 'Failed' : 'No output'}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.created_at ? formatDateTimeIndia(item.created_at) : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.processing_time_seconds ? `${item.processing_time_seconds}s` : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(item)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination - using same pattern as FeedbackManagement.js */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    );
  };

  return (
    <div className="admin-ai-test">
      <h1>🧪 AI Test Module</h1>
      <p className="subtitle">
        Developer testing tool for exploring different AI APIs with custom prompts and S3 image batches
      </p>

      {/* Messages */}
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      {info && <div className="info-message">{info}</div>}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'test-form' ? 'active' : ''}`}
          onClick={() => setActiveTab('test-form')}
        >
          🔧 Test Configuration
        </button>
        <button
          className={`tab-button ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          📊 Results
        </button>
        <button
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          📜 History
        </button>
      </div>

      {/* Test Configuration Tab */}
      {activeTab === 'test-form' && (
        <>
          {/* Generation Type Selection */}
          <div className="generation-type-section">
            <h2>🎯 Generation Type</h2>
            <div className="generation-type-toggle">
              <div className="type-option">
                <input
                  type="radio"
                  id="image-generation"
                  name="generationType"
                  value="image"
                  checked={generationType === 'image'}
                  onChange={(e) => handleGenerationTypeChange(e.target.value)}
                />
                <label htmlFor="image-generation" className="type-label">
                  <div className="type-icon">🖼️</div>
                  <div className="type-content">
                    <div className="type-title">Image Generation</div>
                    <div className="type-description">Generate images directly from input images</div>
                  </div>
                </label>
              </div>
              <div className="type-option">
                <input
                  type="radio"
                  id="video-generation"
                  name="generationType"
                  value="video"
                  checked={generationType === 'video'}
                  onChange={(e) => handleGenerationTypeChange(e.target.value)}
                />
                <label htmlFor="video-generation" className="type-label">
                  <div className="type-icon">🎬</div>
                  <div className="type-content">
                    <div className="type-title">Video Generation</div>
                    <div className="type-description">Two-step process: Image → Video</div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* API Configuration */}
          <div className="api-configuration-section">
            <h2>🔗 API Configuration</h2>

            {generationType === 'image' ? (
              /* Single API Configuration for Image Generation */
              <div className="api-card single-api">
                <div className="api-card-header">
                  <h3>🖼️ Image Generation API</h3>
                  <div className="api-status">
                    {apiTestingStatus.step1.success && <span className="status-success">✅ Connected</span>}
                    {apiTestingStatus.step1.error && <span className="status-error">❌ {apiTestingStatus.step1.error}</span>}
                  </div>
                </div>
                <div className="api-card-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label>API URL *</label>
                      <input
                        type="url"
                        value={formData.api_url}
                        onChange={(e) => handleInputChange('api_url', e.target.value)}
                        placeholder="https://api.example.com/generate-image"
                        className={apiTestingStatus.step1.error ? 'error' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>API Key</label>
                      <input
                        type="password"
                        value={formData.api_key}
                        onChange={(e) => handleInputChange('api_key', e.target.value)}
                        placeholder="Enter API key if required"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Dual API Configuration for Video Generation */
              <div className="video-api-config">
                <div className="api-card step-api">
                  <div className="api-card-header">
                    <div className="step-indicator">Step 1</div>
                    <h3>🖼️ Image Generation API</h3>
                    <div className="api-status">
                      {apiTestingStatus.step1.success && <span className="status-success">✅ Connected</span>}
                      {apiTestingStatus.step1.error && <span className="status-error">❌ {apiTestingStatus.step1.error}</span>}
                    </div>
                  </div>
                  <div className="api-card-body">
                    <div className="form-row">
                      <div className="form-group">
                        <label>API URL *</label>
                        <input
                          type="url"
                          value={formData.step1_api_url}
                          onChange={(e) => handleInputChange('step1_api_url', e.target.value)}
                          placeholder="https://api.example.com/generate-image"
                          className={apiTestingStatus.step1.error ? 'error' : ''}
                        />
                      </div>
                      <div className="form-group">
                        <label>API Key</label>
                        <input
                          type="password"
                          value={formData.step1_api_key}
                          onChange={(e) => handleInputChange('step1_api_key', e.target.value)}
                          placeholder="Enter API key if required"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="step-connector">
                  <div className="connector-line"></div>
                  <div className="connector-arrow">↓</div>
                </div>

                <div className="api-card step-api">
                  <div className="api-card-header">
                    <div className="step-indicator">Step 2</div>
                    <h3>🎬 Video Generation API</h3>
                    <div className="api-status">
                      {apiTestingStatus.step2.success && <span className="status-success">✅ Connected</span>}
                      {apiTestingStatus.step2.error && <span className="status-error">❌ {apiTestingStatus.step2.error}</span>}
                    </div>
                  </div>
                  <div className="api-card-body">
                    <div className="form-row">
                      <div className="form-group">
                        <label>API URL *</label>
                        <input
                          type="url"
                          value={formData.step2_api_url}
                          onChange={(e) => handleInputChange('step2_api_url', e.target.value)}
                          placeholder="https://api.example.com/generate-video"
                          className={apiTestingStatus.step2.error ? 'error' : ''}
                        />
                      </div>
                      <div className="form-group">
                        <label>API Key</label>
                        <input
                          type="password"
                          value={formData.step2_api_key}
                          onChange={(e) => handleInputChange('step2_api_key', e.target.value)}
                          placeholder="Enter API key if required"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="test-form-section">
            <h2>📁 S3 Images</h2>

            {/* Folder Management Section */}
            <div className="folder-management-section">
              <h3>📁 Choose Upload Method</h3>

              {/* Upload Mode Toggle */}
              <div className="upload-mode-toggle">
                <button
                  className={`btn ${uploadMode === 'create_new' && folderUploadMode ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => {
                    setFolderUploadMode(!folderUploadMode);
                    setUploadMode('create_new');
                    // Reset other states when switching to upload new folder
                    setSelectedFolder(null);
                    setFormData(prev => ({ ...prev, s3_folder_path: '' }));
                  }}
                  disabled={isUploading}
                >
                  <span className="btn-icon">📤</span>
                  <span className="btn-text">
                    <strong>Upload New Folder</strong>
                    <small>Create a new folder with images</small>
                  </span>
                </button>
                <button
                  className={`btn ${uploadMode === 'use_existing' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => {
                    setUploadMode('use_existing');
                    setFolderUploadMode(false);
                    setSelectedFolder(null);
                    setFormData(prev => ({ ...prev, s3_folder_path: '' }));
                    setShowFolderBrowser(true);
                    loadExistingFolders();
                  }}
                  disabled={isUploading}
                >
                  <span className="btn-icon">🔍</span>
                  <span className="btn-text">
                    <strong>Use Existing Folder</strong>
                    <small>Browse and select from existing folders</small>
                  </span>
                </button>
                <button
                  className={`btn ${uploadMode === 'append_existing' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleUploadModeChange('append_existing')}
                  disabled={isUploading}
                >
                  <span className="btn-icon">📁</span>
                  <span className="btn-text">
                    <strong>Append to Existing</strong>
                    <small>Add images to a folder you've used before</small>
                  </span>
                </button>
              </div>

              {uploadMode === 'append_existing' && (
                <div className="folder-selection">
                  <div className="selected-folder-info">
                    {selectedFolder ? (
                      <div className="folder-info-card">
                        <span className="folder-icon">📁</span>
                        <div className="folder-details">
                          <div className="folder-name">{selectedFolder.folder_name}</div>
                          <div className="folder-meta">
                            {selectedFolder.image_count} images • {formatFileSize(selectedFolder.total_size)}
                          </div>
                        </div>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => setSelectedFolder(null)}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="no-folder-selected">
                        <span className="folder-icon">📁</span>
                        <span>No folder selected</span>
                      </div>
                    )}
                  </div>

                  <div className="folder-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowFolderBrowser(true)}
                      disabled={isUploading}
                    >
                      {selectedFolder ? '📁 Change Folder' : '📁 Select Folder'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={loadExistingFolders}
                      disabled={foldersLoading}
                    >
                      {foldersLoading ? '🔄 Loading...' : '🔄 Refresh'}
                    </button>
                  </div>
                </div>
              )}

              {uploadMode === 'use_existing' && (
                <div className="folder-selection">
                  <div className="selected-folder-info">
                    {selectedFolder ? (
                      <div className="folder-info-card">
                        <span className="folder-icon">📁</span>
                        <div className="folder-details">
                          <div className="folder-name">{selectedFolder.folder_name}</div>
                          <div className="folder-meta">
                            {selectedFolder.image_count} images • {formatFileSize(selectedFolder.total_size)}
                          </div>
                          <div className="folder-description">
                            S3 Path: {selectedFolder.s3_folder_path}
                          </div>
                        </div>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => setSelectedFolder(null)}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="no-folder-selected">
                        <span className="folder-icon">🔍</span>
                        <span>No folder selected - Click "Browse Folders" to choose one</span>
                      </div>
                    )}
                  </div>

                  <div className="folder-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setShowFolderBrowser(true);
                        loadExistingFolders();
                      }}
                      disabled={isUploading}
                    >
                      🔍 Browse Folders
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={loadExistingFolders}
                      disabled={foldersLoading}
                    >
                      {foldersLoading ? '🔄 Loading...' : '🔄 Refresh'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {(!folderUploadMode && uploadMode !== 'append_existing' && uploadMode !== 'use_existing') ? (
              /* Manual S3 Folder Path Input - Only for create new mode */
              <div className="form-row">
                <div className="form-group">
                  <label>S3 Folder Path *</label>
                  <input
                    type="text"
                    value={formData.s3_folder_path}
                    onChange={(e) => handleInputChange('s3_folder_path', e.target.value)}
                    placeholder="test-images/batch1/"
                  />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'end' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={handleFetchS3Images}
                    disabled={s3Loading || !formData.s3_folder_path.trim()}
                  >
                    {s3Loading ? '�� Loading...' : '📥 Fetch S3 Images'}
                  </button>
                </div>
              </div>
            ) : (
              /* Folder Upload Interface - Show for create new and append existing, but NOT for use_existing */
              uploadMode !== 'use_existing' && (
                <div className="folder-upload-section">
                  {/* Folder Name Input - Only show for create_new mode */}
                  {uploadMode === 'create_new' && (
                    <div className="form-group">
                      <label>Folder Name *</label>
                      <input
                        type="text"
                        value={formData.folder_name}
                        onChange={(e) => handleInputChange('folder_name', e.target.value)}
                        placeholder="Enter folder name (e.g., my_test_images)"
                        disabled={isUploading}
                        style={{
                          padding: '12px',
                          border: '2px solid #e1e5e9',
                          borderRadius: '8px',
                          fontSize: '14px',
                          width: '100%',
                          marginBottom: '16px'
                        }}
                      />
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        💡 This will create: uploads/admin_test/{formData.folder_name || 'your_folder_name'}/
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label>
                      {uploadMode === 'append_existing' ? 'Select Images to Append *' : 'Select Images *'}
                    </label>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <input
                        type="file"
                        webkitdirectory={uploadMode === 'create_new'}
                        directory={uploadMode === 'create_new'}
                        multiple
                        onChange={handleFolderUpload}
                        accept="image/*"
                        disabled={isUploading}
                        style={{
                          position: 'absolute',
                          left: '-9999px',
                          opacity: 0,
                          width: 0,
                          height: 0
                        }}
                        title={uploadMode === 'create_new' ? 'Select images' : 'Select individual image files'}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          const fileInput = e.target.parentElement.querySelector('input[type="file"]');
                          if (fileInput) fileInput.click();
                        }}
                        disabled={isUploading}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#4f46e5',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: isUploading ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          marginBottom: '10px',
                          opacity: isUploading ? 0.6 : 1
                        }}
                      >
                        {uploadMode === 'create_new' ? '📁 Choose Files' : '📄 Choose Files'}
                      </button>
                    </div>
                    {uploadMode === 'create_new' && (
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        💡 Click "Choose Files" to select images
                      </div>
                    )}
                    <div className="file-info">
                      {uploadedFiles.length > 0 && (
                        <div className="selected-files">
                          <strong>Selected Files ({uploadedFiles.length}):</strong>
                          <ul>
                            {uploadedFiles.slice(0, 5).map((file, index) => (
                              <li key={index}>{file.name}</li>
                            ))}
                            {uploadedFiles.length > 5 && (
                              <li>... and {uploadedFiles.length - 5} more files</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="upload-actions">
                    <button
                      className="btn btn-primary"
                      onClick={handleUploadToS3}
                      disabled={isUploading || uploadedFiles.length === 0 || (uploadMode === 'create_new' && !formData.folder_name.trim())}
                    >
                      {isUploading ? '🔄 Uploading...' :
                        uploadMode === 'append_existing' ? `📤 Append ${uploadedFiles.length} Files to Selected Folder` :
                          `📤 Upload ${uploadedFiles.length} Files to "${formData.folder_name || 'New Folder'}"`}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={handleCancelUpload}
                      disabled={isUploading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )
            )}

            {/* Use Existing Folder Interface */}
            {uploadMode === 'use_existing' && selectedFolder && (
              <div className="use-existing-folder-section">
                <div className="form-group">
                  <label>Selected Folder: {selectedFolder.folder_name}</label>
                  <div className="folder-info-card">
                    <span className="folder-icon">📁</span>
                    <div className="folder-details">
                      <div className="folder-name">{selectedFolder.folder_name}</div>
                      <div className="folder-meta">
                        {selectedFolder.image_count} images • {formatFileSize(selectedFolder.total_size)}
                      </div>
                      <div className="folder-description">
                        S3 Path: {selectedFolder.s3_folder_path}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="folder-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleFetchS3Images}
                    disabled={s3Loading}
                  >
                    {s3Loading ? '🔄 Loading...' : '📥 Fetch S3 Images'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedFolder(null)}
                  >
                    ✕ Clear Selection
                  </button>
                </div>
              </div>
            )}

            <div className="s3-preview-section">
              <h3>📷 Images Preview ({s3Images.length} images)</h3>
              {renderS3Preview()}
            </div>
          </div>

          <div className="test-form-section">
            <h2>📝 Generation Parameters</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Guidance Scale</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="20"
                  value={formData.guidance_scale}
                  onChange={(e) => handleInputChange('guidance_scale', parseFloat(e.target.value))}
                />
              </div>
              <div className="form-group">
                <label>Aspect Ratio</label>
                <select
                  value={formData.ratio}
                  onChange={(e) => handleInputChange('ratio', e.target.value)}
                >
                  <option value="1:1">1:1 (Square)</option>
                  <option value="16:9">16:9 (Landscape)</option>
                  <option value="9:16">9:16 (Portrait)</option>
                  <option value="4:3">4:3 (Standard)</option>
                  <option value="3:4">3:4 (Portrait)</option>
                  <option value="3:2">3:2 (Classic Photo)</option>
                  <option value="2:3">2:3 (Classic Portrait)</option>
                  <option value="21:9">21:9 (Ultrawide)</option>
                  <option value="9:21">9:21 (Ultrawide Portrait)</option>
                  <option value="5:4">5:4 (Art/Photo)</option>
                  <option value="4:5">4:5 (Portrait)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Resolution</label>
                <select
                  value={formData.resolution}
                  onChange={(e) => handleInputChange('resolution', e.target.value)}
                >
                  <option value="1k">1k</option>
                  <option value="2k">2k</option>
                  <option value="4k">4k</option>
                </select>
              </div>
            </div>

            {/* Universal Prompt Library - Available for both Image and Video modes */}
            <div className="prompt-library-card">
              <div className="card-header">
                <div className="header-title">
                  <span className="icon">📚</span>
                  <h4>Prompt Library</h4>
                </div>
                {promptLoading && <span className="loading-tag">Processing...</span>}
              </div>

              <div className="card-body">
                {/* Primary Row: Template Selection */}
                <div className="control-row main-row">
                  <div className="control-group flex-grow">
                    <label htmlFor="template-autocomplete">Select Template</label>
                    <Autocomplete
                      id="template-autocomplete"
                      options={promptLibrary}
                      getOptionLabel={(option) => {
                        const versionInfo = option.version ? `(v${option.version})` : '';
                        const categoryInfo = option.category_name ? `(${option.category_name})` : '';
                        return `${option.display_name} ${versionInfo} ${categoryInfo}`.trim();
                      }}
                      value={promptLibrary.find(p => p.id === parseInt(selectedPromptId)) || null}
                      onChange={(event, newValue) => {
                        handlePromptSelection(newValue ? newValue.id : '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search or choose a template..."
                          size="small"
                          className="premium-autocomplete-input"
                          disabled={promptLoading}
                        />
                      )}
                      disabled={promptLoading}
                      fullWidth
                    />
                  </div>
                  <div className="control-group action-group">
                    <label>&nbsp;</label>
                    <button
                      className="btn btn-secondary refresh-btn-premium"
                      onClick={handleRefreshPrompt}
                      disabled={!selectedPromptId || promptLoading}
                      title="Generate new random values for variables"
                    >
                      <span className="icon">🔄</span>
                      Refresh
                    </button>
                  </div>
                </div>

                {/* Secondary Row: Dynamic attributes based on model type (Human vs Bust) */}
                <div className="control-row attribute-row">
                  {(!selectedPromptId || !(promptLibrary.find(p => p.id === parseInt(selectedPromptId))?.model_type_name === 'Bust Display' || promptLibrary.find(p => p.id === parseInt(selectedPromptId))?.model_type_id === 22)) ? (
                    <>
                      {/* Human Model Attributes */}
                      <div className="control-group">
                        <label htmlFor="ethnicity-select">
                          <span className="label-icon">👤</span> Ethnicity
                        </label>
                        <select
                          id="ethnicity-select"
                          value={selectedEthnicity}
                          onChange={(e) => {
                            setSelectedEthnicity(e.target.value);
                            if (selectedPromptId) {
                              const selectedPrompt = promptLibrary.find(p => p.id === parseInt(selectedPromptId));
                              if (selectedPrompt) {
                                processPromptWithVariables(
                                  selectedPrompt.prompt_template,
                                  selectedPrompt.dictionary_data,
                                  parseInt(selectedPromptId),
                                  { ethnicity_id: e.target.value, clothing_type_id: selectedClothingType }
                                );
                              }
                            }
                          }}
                          className="premium-select-sm"
                          disabled={promptLoading}
                        >
                          <option value="">Randomized</option>
                          {masterData.ethnicities.map(e => (
                            <option key={e.id} value={e.id}>{e.display_name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="control-group">
                        <label htmlFor="clothing-select">
                          <span className="label-icon">👗</span> Clothing Type
                        </label>
                        <select
                          id="clothing-select"
                          value={selectedClothingType}
                          onChange={(e) => {
                            setSelectedClothingType(e.target.value);
                            if (selectedPromptId) {
                              const selectedPrompt = promptLibrary.find(p => p.id === parseInt(selectedPromptId));
                              if (selectedPrompt) {
                                processPromptWithVariables(
                                  selectedPrompt.prompt_template,
                                  selectedPrompt.dictionary_data,
                                  parseInt(selectedPromptId),
                                  { ethnicity_id: selectedEthnicity, clothing_type_id: e.target.value }
                                );
                              }
                            }
                          }}
                          className="premium-select-sm"
                          disabled={promptLoading}
                        >
                          <option value="">Randomized</option>
                          {masterData.clothing_types.map(c => (
                            <option key={c.id} value={c.id}>{c.display_name}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Bust Display Attributes */}
                      <div className="control-group">
                        <label htmlFor="bust-finish-select">
                          <span className="label-icon">🗿</span> Bust Finish
                        </label>
                        <select
                          id="bust-finish-select"
                          value={selectedBustFinish}
                          onChange={(e) => {
                            setSelectedBustFinish(e.target.value);
                            if (selectedPromptId) {
                              const selectedPrompt = promptLibrary.find(p => p.id === parseInt(selectedPromptId));
                              if (selectedPrompt) {
                                processPromptWithVariables(
                                  selectedPrompt.prompt_template,
                                  selectedPrompt.dictionary_data,
                                  parseInt(selectedPromptId),
                                  { bust_finish_id: e.target.value, bust_color_id: selectedBustColor }
                                );
                              }
                            }
                          }}
                          className="premium-select-sm"
                          disabled={promptLoading}
                        >
                          <option value="">Randomized</option>
                          {masterData.bust_finishes.map(b => (
                            <option key={b.id} value={b.id}>{b.display_name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="control-group">
                        <label htmlFor="bust-color-select">
                          <span className="label-icon">🎨</span> Bust Color
                        </label>
                        <select
                          id="bust-color-select"
                          value={selectedBustColor}
                          onChange={(e) => {
                            setSelectedBustColor(e.target.value);
                            if (selectedPromptId) {
                              const selectedPrompt = promptLibrary.find(p => p.id === parseInt(selectedPromptId));
                              if (selectedPrompt) {
                                processPromptWithVariables(
                                  selectedPrompt.prompt_template,
                                  selectedPrompt.dictionary_data,
                                  parseInt(selectedPromptId),
                                  { bust_finish_id: selectedBustFinish, bust_color_id: e.target.value }
                                );
                              }
                            }
                          }}
                          className="premium-select-sm"
                          disabled={promptLoading}
                        >
                          <option value="">Randomized</option>
                          {masterData.bust_colors.map(bc => (
                            <option key={bc.id} value={bc.id}>{bc.display_name}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Variable Preview Section - shows replaced variables in bold */}
            {processedPrompt && replacedVariables.length > 0 && (
              <div className="prompt-preview-section">
                <label>📝 Preview (replaced variables highlighted):</label>
                <div className="preview-text">
                  {renderPromptWithHighlights()}
                </div>
                <div className="variables-list">
                  <small>
                    Variables replaced: {replacedVariables.map(v => v.variable).join(', ')}
                  </small>
                </div>
              </div>
            )}

            {/* Mode-Specific Prompt Sections */}
            {generationType === 'image' ? (
              <div className="prompt-section-enhanced">
                {/* Prompt Textarea */}
                <div className="form-row single">
                  <div className="form-group">
                    <label>Prompt *</label>
                    <textarea
                      value={formData.prompt}
                      onChange={(e) => handleInputChange('prompt', e.target.value)}
                      placeholder="Enter your image generation prompt..."
                      rows={4}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="video-prompts-section">
                <div className="prompt-card">
                  <div className="prompt-header">
                    <div className="step-indicator">Step 1</div>
                    <h4>🖼️ Image Enhancement Prompt</h4>
                  </div>
                  <div className="form-group">
                    <label>Image Prompt *</label>
                    <textarea
                      value={formData.image_prompt_1}
                      onChange={(e) => handleInputChange('image_prompt_1', e.target.value)}
                      placeholder="Describe how to enhance the input image..."
                      rows={3}
                    />
                  </div>
                </div>

                <div className="step-connector">
                  <div className="connector-line"></div>
                  <div className="connector-arrow">↓</div>
                </div>

                <div className="prompt-card">
                  <div className="prompt-header">
                    <div className="step-indicator">Step 2</div>
                    <h4>🎬 Video Generation Prompt</h4>
                  </div>
                  <div className="form-group">
                    <label>Video Prompt *</label>
                    <textarea
                      value={formData.video_prompt}
                      onChange={(e) => handleInputChange('video_prompt', e.target.value)}
                      placeholder="Describe the video you want to generate from the enhanced image..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="action-buttons">
            {canCreate && (
              <button
                className="btn btn-primary"
                onClick={handleStartGeneration}
                disabled={isLoading || s3Images.length === 0}
              >
                {isLoading ? '🔄 Starting...' : `🚀 Start ${generationType === 'image' ? 'Image' : 'Video'} Generation`}
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={() => setActiveTab('results')}
            >
              📊 View Results
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setActiveTab('history')}
            >
              📜 View History
            </button>
          </div>
        </>
      )}

      {/* Results Tab */}
      {activeTab === 'results' && (
        <div className="results-section">
          <div className="results-header">
            <h2>📊 Generation Results</h2>
            {currentBatch && (
              <button
                className="btn btn-outlined"
                onClick={handleClearBatch}
                title="Clear current batch and stop monitoring"
              >
                Clear Batch
              </button>
            )}
          </div>
          {renderBatchStatus()}
          {renderGenerationResults()}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="history-section">
          <div className="history-header">
            <h2>📜 Test History</h2>
            <button
              className="btn btn-outlined"
              onClick={() => setHistoryFilters({
                status: '',
                output_format: '',
                date_from: '',
                date_to: ''
              })}
              title="Click to clear all filters and show all results"
            >
              Clear Filter
            </button>
          </div>

          {/* History Filters */}
          <div className="history-filters">
            <div className="form-group">
              <label>Status</label>
              <select
                value={historyFilters.status}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                <option value="COMPLETED">Completed</option>
                <option value="FAILED">Failed</option>
                <option value="PROCESSING">Processing</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>
            <div className="form-group">
              <label>Output Format</label>
              <select
                value={historyFilters.output_format}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, output_format: e.target.value }))}
              >
                <option value="">All Formats</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date From</label>
              <input
                type="date"
                value={historyFilters.date_from}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, date_from: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Date To</label>
              <input
                type="date"
                value={historyFilters.date_to}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, date_to: e.target.value }))}
              />
            </div>
          </div>

          {renderHistoryTable()}
        </div>
      )}

      {/* Image Dialog */}
      {imageDialogOpen && (
        <div className="image-modal-overlay" onClick={handleCloseImageDialog}>
          <div className="modal-content image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedImage?.title}</h3>
              <button className="close-btn" onClick={handleCloseImageDialog}>×</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center' }}>
              {selectedImage && (
                <>
                  {selectedImage.url.includes('/api/admin/ai-test/preview/') ? (
                    // Check if it's a video based on title or URL
                    selectedImage.title?.toLowerCase().includes('video') ||
                      selectedImage.title?.toLowerCase().includes('generated video') ||
                      selectedImage.url.toLowerCase().includes('.mp4') ||
                      selectedImage.url.toLowerCase().includes('video') ||
                      selectedImage.url.toLowerCase().includes('generated_video') ? (
                      <SecureVideo
                        src={selectedImage.url}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '80vh',
                          objectFit: 'contain',
                          display: 'block',
                          margin: '0 auto'
                        }}
                        controls
                        autoPlay={false}
                      />
                    ) : (
                      <SecureImage
                        src={selectedImage.url}
                        alt={selectedImage.title}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '80vh',
                          objectFit: 'contain',
                          display: 'block',
                          margin: '0 auto'
                        }}
                      />
                    )
                  ) : (
                    // Check if it's a video based on title or URL
                    selectedImage.title?.toLowerCase().includes('video') ||
                      selectedImage.title?.toLowerCase().includes('generated video') ||
                      selectedImage.url.toLowerCase().includes('.mp4') ||
                      selectedImage.url.toLowerCase().includes('video') ||
                      selectedImage.url.toLowerCase().includes('generated_video') ? (
                      <video
                        src={selectedImage.url}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '80vh',
                          objectFit: 'contain',
                          display: 'block',
                          margin: '0 auto'
                        }}
                        controls
                        autoPlay={false}
                      />
                    ) : (
                      <img
                        src={selectedImage.url}
                        alt={selectedImage.title}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '80vh',
                          objectFit: 'contain',
                          display: 'block',
                          margin: '0 auto'
                        }}
                      />
                    )
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Folder Browser Modal */}
      {showFolderBrowser && (
        <div className="modal-overlay" onClick={() => setShowFolderBrowser(false)}>
          <div className="modal-content folder-browser" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📁 Select Test Data Folder</h3>
              <button className="close-btn" onClick={() => setShowFolderBrowser(false)}>×</button>
            </div>
            <div className="modal-body">
              {foldersLoading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading folders...</p>
                </div>
              ) : existingFolders.length === 0 ? (
                <div className="empty-state">
                  <p>No folders found. Create a new folder first.</p>
                </div>
              ) : (
                <div className="folder-list">
                  {existingFolders.map((folder) => (
                    <div
                      key={folder.s3_folder_path}
                      className={`folder-item ${selectedFolder?.s3_folder_path === folder.s3_folder_path ? 'selected' : ''}`}
                      onClick={() => handleFolderSelection(folder)}
                    >
                      <div className="folder-icon">📁</div>
                      <div className="folder-info">
                        <div className="folder-name">{folder.folder_name}</div>
                        <div className="folder-meta">
                          {folder.image_count} images • {formatFileSize(folder.total_size)} • {formatDate(folder.last_used || folder.created_at)}
                        </div>
                        {folder.description && (
                          <div className="folder-description">{folder.description}</div>
                        )}
                        {folder.is_recent && (
                          <div className="recent-badge">Recent</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowFolderBrowser(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Details Dialog */}
      {detailsDialogOpen && (
        <div className="modal-overlay" onClick={handleCloseDetailsDialog}>
          <div className={`modal-content details-modal ${collageMode ? 'collage-mode' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Generation Details - {selectedHistoryItem?.batch_id || `#${selectedHistoryItem?.id}`}</h3>
              <div className="header-actions">
                <button
                  className={`btn-toggle-view ${collageMode ? 'active' : ''}`}
                  onClick={handleCollageModeToggle}
                  title={collageMode ? 'Switch to regular view' : 'Switch to collage view'}
                >
                  {collageMode ? '📋 Regular View' : '🖼️ Collage View'}
                </button>
                {collageMode && (
                  <div className="zoom-controls">
                    <button onClick={handleZoomOut} className="btn-zoom" title="Zoom Out">−</button>
                    <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
                    <button onClick={handleZoomIn} className="btn-zoom" title="Zoom In">+</button>
                    <button onClick={handleZoomReset} className="btn-zoom-reset" title="Reset Zoom">⟳</button>
                  </div>
                )}
                <button className="close-btn" onClick={handleCloseDetailsDialog}>×</button>
              </div>
            </div>
            <div className="modal-body">
              {selectedHistoryItem && (
                <div className="details-content">
                  {!collageMode && (
                    /* Status and Basic Info - only show in regular view */
                    <div className="details-header">
                      <span className={`status-badge ${selectedHistoryItem.status.toLowerCase()}`}>
                        {selectedHistoryItem.status}
                      </span>
                      <span className="format-info">Format: {selectedHistoryItem.output_format}</span>
                      {selectedHistoryItem.processing_time_seconds && (
                        <span className="time-info">Time: {selectedHistoryItem.processing_time_seconds}s</span>
                      )}
                    </div>
                  )}

                  {/* Images Section */}
                  {collageMode ? (
                    /* Collage View */
                    <div className="collage-section">
                      <div
                        className="collage-wrapper"
                        style={{
                          transform: `scale(${zoomLevel})`,
                          transformOrigin: 'top left',
                          width: '100%',
                          height: '100%'
                        }}
                      >
                        <div className="collage-container">
                          <div className="collage-item">
                            <div className="collage-label">Input Image</div>
                            <div className="collage-image-wrapper">
                              {(selectedHistoryItem.original_image_path || selectedHistoryItem.input_image_path) ? (
                                imageLoadErrors[selectedHistoryItem.original_image_path || selectedHistoryItem.input_image_path] ? (
                                  <div className="no-image-placeholder">
                                    <span>Input image failed to load</span>
                                  </div>
                                ) : (
                                  <SecureImage
                                    src={getSecureAdminAITestUrl(selectedHistoryItem.original_image_path || selectedHistoryItem.input_image_path, selectedHistoryItem.id, true)}
                                    alt="Input"
                                    className="collage-image"
                                    onError={() => handleImageError(selectedHistoryItem.original_image_path || selectedHistoryItem.input_image_path, 'Input')}
                                    onClick={() => handleImageClick(
                                      getSecureAdminAITestUrl(selectedHistoryItem.original_image_path || selectedHistoryItem.input_image_path, selectedHistoryItem.id, true),
                                      'Input Image'
                                    )}
                                    onLoad={() => handleCollageImageLoad('input')}
                                  />
                                )
                              ) : (
                                <div className="no-image-placeholder">
                                  <span>No input image</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="collage-divider"></div>
                          <div className="collage-item">
                            <div className="collage-label">
                              {selectedHistoryItem.output_format === 'video' ? 'Video Output' : 'Image Output'}
                            </div>
                            <div className="collage-image-wrapper">
                              {selectedHistoryItem.output_format === 'video' ? (
                                (selectedHistoryItem.generated_video_path || selectedHistoryItem.output_video_path) ? (
                                  imageLoadErrors[selectedHistoryItem.generated_video_path || selectedHistoryItem.output_video_path] ? (
                                    <div className="no-image-placeholder">
                                      <span>Video failed to load</span>
                                    </div>
                                  ) : (
                                    <SecureVideo
                                      src={getSecureAdminAITestUrl(selectedHistoryItem.generated_video_path || selectedHistoryItem.output_video_path, selectedHistoryItem.id)}
                                      className="collage-image collage-video"
                                      controls
                                      onError={() => handleImageError(selectedHistoryItem.generated_video_path || selectedHistoryItem.output_video_path, 'Video')}
                                      onClick={() => handleImageClick(
                                        getSecureAdminAITestUrl(selectedHistoryItem.generated_video_path || selectedHistoryItem.output_video_path, selectedHistoryItem.id),
                                        'Generated Video'
                                      )}
                                    />
                                  )
                                ) : (
                                  <div className="no-image-placeholder">
                                    <span>No video output</span>
                                  </div>
                                )
                              ) : (selectedHistoryItem.generated_image_path || selectedHistoryItem.output_image_path) ? (
                                imageLoadErrors[selectedHistoryItem.generated_image_path || selectedHistoryItem.output_image_path] ? (
                                  <div className="no-image-placeholder">
                                    <span>Image failed to load</span>
                                  </div>
                                ) : (
                                  <SecureImage
                                    src={getSecureAdminAITestUrl(selectedHistoryItem.generated_image_path || selectedHistoryItem.output_image_path, selectedHistoryItem.id)}
                                    alt="Output"
                                    className="collage-image"
                                    onError={() => handleImageError(selectedHistoryItem.generated_image_path || selectedHistoryItem.output_image_path, 'Generated')}
                                    onClick={() => handleImageClick(
                                      getSecureAdminAITestUrl(selectedHistoryItem.generated_image_path || selectedHistoryItem.output_image_path, selectedHistoryItem.id),
                                      'Generated Image'
                                    )}
                                    onLoad={() => handleCollageImageLoad('output')}
                                  />
                                )
                              ) : (
                                <div className="no-image-placeholder">
                                  <span>No output image</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Regular View */
                    <div className="images-section">
                      {/* Input Image */}
                      <div className="image-container">
                        <h4>Input Image</h4>
                        {(selectedHistoryItem.original_image_path || selectedHistoryItem.input_image_path) ? (
                          <div
                            className="image-preview"
                            onClick={() => handleImageClick(
                              getSecureAdminAITestUrl(selectedHistoryItem.original_image_path || selectedHistoryItem.input_image_path, selectedHistoryItem.id, true),
                              'Input Image'
                            )}
                          >
                            {imageLoadErrors[selectedHistoryItem.original_image_path || selectedHistoryItem.input_image_path] ? (
                              <div className="no-image">Input image failed to load</div>
                            ) : (
                              <SecureImage
                                src={getSecureAdminAITestUrl(selectedHistoryItem.original_image_path || selectedHistoryItem.input_image_path, selectedHistoryItem.id, true)}
                                alt="Input"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain',
                                  cursor: 'pointer'
                                }}
                                onError={() => handleImageError(selectedHistoryItem.original_image_path || selectedHistoryItem.input_image_path, 'Input')}
                              />
                            )}
                          </div>
                        ) : (
                          <div className="no-image">No input image</div>
                        )}
                      </div>

                      {/* Step 1 Result - Enhanced Image */}
                      {selectedHistoryItem.output_format === 'video' && selectedHistoryItem.enhanced_image_path && (
                        <div className="image-container">
                          <h4>Step 1: Enhanced Image</h4>
                          <div
                            className="image-preview"
                            onClick={() => handleImageClick(
                              getSecureAdminAITestUrl(selectedHistoryItem.enhanced_image_path, selectedHistoryItem.id, false, 'enhanced'),
                              'Step 1: Enhanced Image'
                            )}
                          >
                            {imageLoadErrors[selectedHistoryItem.enhanced_image_path] ? (
                              <div className="no-image">Step 1 image failed to load</div>
                            ) : (
                              <SecureImage
                                src={getSecureAdminAITestUrl(selectedHistoryItem.enhanced_image_path, selectedHistoryItem.id, false, 'enhanced')}
                                alt="Step 1 Enhanced"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain',
                                  cursor: 'pointer'
                                }}
                                onError={() => handleImageError(selectedHistoryItem.enhanced_image_path, 'Step 1 Enhanced')}
                              />
                            )}
                          </div>
                        </div>
                      )}

                      {/* Final Output - Video or Image */}
                      <div className="image-container">
                        <h4>Final Output: {selectedHistoryItem.output_format === 'video' ? 'Video' : 'Image'}</h4>
                        {(selectedHistoryItem.generated_image_path || selectedHistoryItem.generated_video_path || selectedHistoryItem.output_image_path || selectedHistoryItem.output_video_path) ? (
                          <div
                            className="image-preview"
                            onClick={() => handleImageClick(
                              getSecureAdminAITestUrl(selectedHistoryItem.generated_image_path || selectedHistoryItem.generated_video_path || selectedHistoryItem.output_image_path || selectedHistoryItem.output_video_path, selectedHistoryItem.id, false),
                              `Final Output ${selectedHistoryItem.output_format === 'video' ? 'Video' : 'Image'}`
                            )}
                          >
                            {selectedHistoryItem.output_format === 'video' ? (
                              imageLoadErrors[selectedHistoryItem.generated_video_path || selectedHistoryItem.output_video_path] ? (
                                <div className="no-image">Final video failed to load</div>
                              ) : (
                                <SecureVideo
                                  src={getSecureAdminAITestUrl(selectedHistoryItem.generated_video_path || selectedHistoryItem.output_video_path, selectedHistoryItem.id)}
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    cursor: 'pointer'
                                  }}
                                  controls
                                  onError={() => handleImageError(selectedHistoryItem.generated_video_path || selectedHistoryItem.output_video_path, 'Final Video')}
                                />
                              )
                            ) : (
                              imageLoadErrors[selectedHistoryItem.generated_image_path || selectedHistoryItem.output_image_path] ? (
                                <div className="no-image">Final image failed to load</div>
                              ) : (
                                <SecureImage
                                  src={getSecureAdminAITestUrl(selectedHistoryItem.generated_image_path || selectedHistoryItem.output_image_path, selectedHistoryItem.id)}
                                  alt="Final Output"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    cursor: 'pointer'
                                  }}
                                  onError={() => handleImageError(selectedHistoryItem.generated_image_path || selectedHistoryItem.output_image_path, 'Final Image')}
                                />
                              )
                            )}
                          </div>
                        ) : (
                          <div className="no-image">
                            <div>No output generated</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Details Section - only show in regular view */}
                  {!collageMode && (
                    <div className="details-section">
                      <h4>Generation Details</h4>
                      <div className="details-list">
                        <div className="detail-item">
                          <strong>Format:</strong> {selectedHistoryItem.output_format}
                        </div>
                        <div className="detail-item">
                          <strong>Prompt:</strong> {selectedHistoryItem.prompt || 'No prompt provided'}
                        </div>
                        {selectedHistoryItem.output_format === 'video' && (
                          <>
                            <div className="detail-item">
                              <strong>Image Prompt (Step 1):</strong> {selectedHistoryItem.image_prompt_1 || 'No prompt provided'}
                            </div>
                            <div className="detail-item">
                              <strong>Video Prompt (Step 2):</strong> {selectedHistoryItem.video_prompt || 'No prompt provided'}
                            </div>
                          </>
                        )}

                        {/* Generation Parameters */}
                        <div className="detail-item">
                          <strong>Guidance Scale:</strong> {selectedHistoryItem.guidance_scale ? selectedHistoryItem.guidance_scale : 'N/A'}
                        </div>
                        <div className="detail-item">
                          <strong>Aspect Ratio:</strong> {selectedHistoryItem.ratio ? selectedHistoryItem.ratio : 'N/A'}
                        </div>
                        <div className="detail-item">
                          <strong>API URL:</strong> {selectedHistoryItem.model_endpoint ? (
                            <span className="api-url" title={selectedHistoryItem.model_endpoint}>
                              {selectedHistoryItem.model_endpoint.length > 50
                                ? `${selectedHistoryItem.model_endpoint.substring(0, 50)}...`
                                : selectedHistoryItem.model_endpoint}
                            </span>
                          ) : 'N/A'}
                        </div>

                        <div className="detail-item">
                          <strong>Created:</strong> {selectedHistoryItem.created_at ? formatDateTimeIndia(selectedHistoryItem.created_at) : 'N/A'}
                        </div>
                        <div className="detail-item">
                          <strong>Completed:</strong> {selectedHistoryItem.completed_at ? formatDateTimeIndia(selectedHistoryItem.completed_at) : 'N/A'}
                        </div>
                        <div className="detail-item">
                          <strong>Processing Time:</strong> {selectedHistoryItem.processing_time_seconds ? `${selectedHistoryItem.processing_time_seconds} seconds` : 'N/A'}
                        </div>
                        {selectedHistoryItem.batch_id && (
                          <div className="detail-item">
                            <strong>Batch ID:</strong> {selectedHistoryItem.batch_id}
                          </div>
                        )}
                        {selectedHistoryItem.error_message && (
                          <div className="detail-item error">
                            <strong>Error Message:</strong> {selectedHistoryItem.error_message}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {collageMode && selectedHistoryItem?.output_format !== 'video' && (
                <button className="btn btn-primary" onClick={handleDownloadCollage} title="Download collage as PNG">
                  💾 Download Collage
                </button>
              )}
              <button className="btn btn-secondary" onClick={handleCloseDetailsDialog}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAITest;

